import React, { useMemo, useState } from "react";
import "../styles/TagsFilter.css";

/**
 * Book-tags filter UI (matches Edit Book tags picker).
 * selected: array of tag_id values (string or number)
 * tagOptions: [{ tag_id, tag_name }]
 * onChange: (nextSelectedIds: Array) => void
 */
const TagsFilter = ({ tagOptions = [], selected = [], onChange }) => {
  const [query, setQuery] = useState("");

  const selectedSet = useMemo(
    () => new Set(selected.map((id) => String(id))),
    [selected]
  );

  const selectedTags = useMemo(
    () =>
      tagOptions.filter((t) => selectedSet.has(String(t.tag_id))),
    [tagOptions, selectedSet]
  );

  const availableTags = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tagOptions
      .filter((t) => !selectedSet.has(String(t.tag_id)))
      .filter((t) => !q || String(t.tag_name || "").toLowerCase().includes(q))
      .sort((a, b) =>
        String(a.tag_name).localeCompare(String(b.tag_name), undefined, {
          sensitivity: "base",
        })
      );
  }, [tagOptions, selectedSet, query]);

  const addTag = (tag) => {
    if (!tag?.tag_id && tag?.tag_id !== 0) return;
    const id = String(tag.tag_id);
    if (selectedSet.has(id)) return;
    onChange([...selected.map(String), id]);
  };

  const removeTag = (tag) => {
    const id = String(tag.tag_id);
    onChange(selected.map(String).filter((v) => v !== id));
  };

  const tryAddFromInput = () => {
    const q = query.trim().toLowerCase();
    if (!q) return;
    const exact = tagOptions.find(
      (t) => String(t.tag_name || "").toLowerCase() === q
    );
    if (exact) {
      addTag(exact);
      setQuery("");
      return;
    }
    const first = availableTags[0];
    if (first) {
      addTag(first);
      setQuery("");
    }
  };

  if (!tagOptions.length) {
    return (
      <div className="tags-filter">
        <p className="tags-filter-empty">No tags available yet.</p>
      </div>
    );
  }

  return (
    <div className="tags-filter">
      <div className="tags-filter-input-wrap">
        <input
          type="text"
          className="tags-filter-input"
          placeholder="Search tags…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              tryAddFromInput();
            }
          }}
        />
        <button
          type="button"
          className="tags-filter-add-btn"
          onClick={tryAddFromInput}
        >
          + Add
        </button>
      </div>

      {selectedTags.length > 0 ? (
        <div className="tags-filter-selected">
          {selectedTags.map((tag) => (
            <div key={tag.tag_id} className="tags-filter-selected-pill">
              <span>{tag.tag_name}</span>
              <button
                type="button"
                className="tags-filter-remove"
                onClick={() => removeTag(tag)}
                aria-label={`Remove ${tag.tag_name}`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      ) : null}

      <p className="tags-filter-heading">
        {query.trim() ? "Matching tags" : "All tags"}
      </p>
      {availableTags.length > 0 ? (
        <div className="tags-filter-list">
          {availableTags.map((t) => (
            <button
              key={t.tag_id}
              type="button"
              className="tags-filter-item"
              onClick={() => addTag(t)}
            >
              {t.tag_name}
            </button>
          ))}
        </div>
      ) : (
        <p className="tags-filter-empty">
          {query.trim()
            ? "No matching tags."
            : "All tags are already selected."}
        </p>
      )}
    </div>
  );
};

export default TagsFilter;

export function bookMatchesSelectedTags(bookOrGroup, selectedTagIds) {
  if (!selectedTagIds?.length) return true;
  const tags = bookOrGroup?.tags || [];
  if (!tags.length) return false;
  const selected = new Set(selectedTagIds.map(String));
  return tags.some(
    (t) =>
      selected.has(String(t.tag_id)) ||
      selected.has(String(t.tag_name || "").toLowerCase())
  );
}
